import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle, Clock, CalendarCheck, Flag, MessageCircle, ChevronLeft, ClipboardCheck, ImageIcon, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { ChatPanel } from "@/features/chat/ChatPanel";
import { MutualDeleteBar } from "./MutualDeleteBar";
import { CalendarButtons } from "@/features/schedule/CalendarButtons";
import type { Case } from "@shitamy/types";
import { withTax } from "@/lib/fee";

interface Props { onOpenCase: (id: string) => void; }

export function MyBidsPage({ onOpenCase }: Props) {
  const [chatCaseId, setChatCaseId] = useState<string | null>(null);
  // チャット開始前の「機器を業者と最終確認」ステップ（必須）。確認済みになるまでチャット本体を表示しない。
  const [equipConfirmed, setEquipConfirmed] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-bids"],
    queryFn: () => api.listMyBids(),
    refetchInterval: 30_000,
  });

  const caseIds = (data ?? []).map((b) => b.case_id);
  // 顧客名（〇様案件）表示用
  const { data: briefs } = useQuery({
    queryKey: ["my-bids-briefs", caseIds],
    queryFn: () => api.casesBrief(caseIds),
    enabled: caseIds.length > 0,
  });
  // 代表写真（1枚目）サムネイル
  const { data: thumbs } = useQuery({
    queryKey: ["my-bids-thumbs", caseIds],
    queryFn: () => api.firstCasePhotoUrls(caseIds),
    enabled: caseIds.length > 0,
    refetchInterval: 300_000,
  });

  // 機器の初回確認は「案件ごとに1回だけ」。確認済みは記憶し、次回以降はそのままチャットを開く。
  const equipKey = (id: string) => `sitame_v_equipok_${id}`;
  const openChat = (id: string) => {
    let done = false;
    try { done = localStorage.getItem(equipKey(id)) === "1"; } catch { /* noop */ }
    setEquipConfirmed(done);
    setChatCaseId(id);
  };
  const markEquipConfirmed = (id: string) => {
    try { localStorage.setItem(equipKey(id), "1"); } catch { /* noop */ }
    setEquipConfirmed(true);
  };

  // ===== 別画面：チャット =====
  if (chatCaseId) {
    const cbrief = briefs?.[chatCaseId];
    const cname = cbrief?.customer_name ? `${cbrief.customer_name}様` : "お客様";
    return (
      <div className="flex flex-col h-full">
        <div className="sticky top-0 z-10 bg-white dark:bg-[var(--color-bg)] border-b border-[var(--color-border)] px-4 py-3 flex items-center gap-2">
          <button onClick={() => setChatCaseId(null)} className="p-1 -ml-1"><ChevronLeft size={20} /></button>
          <div>
            <p className="text-sm font-bold leading-tight">{cname}</p>
            <p className="text-[11px] text-ink-soft dark:text-white/60">案件 {chatCaseId.slice(0, 8)} ／ 工事の打ち合わせ</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {equipConfirmed ? (
            <ChatPanel
              caseId={chatCaseId}
              myRole="vendor"
              title={`${cname}とのチャット`}
              footer={<AcceptedCaseActions caseId={chatCaseId} onOpenChat={() => {}} inChat />}
            />
          ) : (
            <EquipmentConfirmGate onConfirm={() => markEquipConfirmed(chatCaseId)} />
          )}
        </div>
      </div>
    );
  }

  if (isLoading) return <p className="p-4 text-sm">読み込み中...</p>;
  if (error) return <p className="p-4 text-sm text-red-600">読み込み失敗</p>;
  if (!data || data.length === 0) {
    return <p className="p-6 text-sm text-ink-soft text-center">まだ入札がありません</p>;
  }

  return (
    <ul className="divide-y divide-[var(--color-border)]">
      {data.map((b) => {
        const brief = briefs?.[b.case_id];
        const name = brief?.customer_name ? `${brief.customer_name}案件` : "お客様案件";
        const mgmtNo = brief?.case_no ? `No.${String(brief.case_no).padStart(4, "0")}` : `#${b.case_id.slice(0, 6)}`;
        const site = [brief?.prefecture, brief?.city, brief?.address].filter(Boolean).join("") || "現場住所 未設定";
        // 受注確定（accepted）は下の受注フローで操作する。行タップで応札フォーム（空＝¥0）に飛ばさない。
        const navigable = b.status !== "accepted";
        return (
          <li key={b.id}>
            <div
              onClick={() => { if (navigable) onOpenCase(b.case_id); }}
              className={`w-full text-left px-4 py-3 flex items-start gap-3 ${navigable ? "cursor-pointer hover:bg-brand-50 dark:hover:bg-brand-800/20" : ""}`}
            >
              <CaseThumb url={thumbs?.[b.case_id]} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-black tabular-nums px-1.5 py-0.5 rounded bg-brand-500 text-white">{mgmtNo}</span>
                  <span className="text-sm font-bold text-brand-700 dark:text-brand-200 truncate">{name}</span>
                </div>
                <p className="text-xs text-ink-soft dark:text-white/60 mt-0.5 truncate">📍{site}</p>
                <p className="text-sm font-medium mt-0.5">¥{withTax(b.amount).toLocaleString("ja-JP")}（税込）{b.proposed_date ? ` ／ 希望日 ${new Date(b.proposed_date).toLocaleDateString("ja-JP")}` : ""}</p>
                <p className="text-xs text-ink-soft flex items-center gap-1"><StatusIcon status={b.status} size={12} /> {labelOf(b.status)}</p>
              </div>
            </div>
            {/* 受注確定（accepted）→ 工事日確定・完了報告の後フロー */}
            {b.status === "accepted" && <AcceptedCaseActions caseId={b.case_id} onOpenChat={() => openChat(b.case_id)} />}
          </li>
        );
      })}
    </ul>
  );
}

/** 受注後フロー：awarded→工事日確定(scheduled)→完了報告(completed)。チャットは別画面。 */
function AcceptedCaseActions({ caseId, onOpenChat, inChat = false }: { caseId: string; onOpenChat: () => void; inChat?: boolean }) {
  const qc = useQueryClient();
  const [date, setDate] = useState("");
  // 実機の最終確認値（品番/能力/冷媒）。完了報告と一緒に送信し、AI学習の正解ラベルになる（sql/21 P0）。
  const [vcModel, setVcModel] = useState("");
  const [vcCapacity, setVcCapacity] = useState("");
  const [vcRefrigerant, setVcRefrigerant] = useState("");
  const [vcPrefilled, setVcPrefilled] = useState(false);
  const { data: c, isLoading } = useQuery({
    queryKey: ["case", caseId],
    queryFn: () => api.getCase(caseId),
    refetchInterval: 30_000,
  });

  // AI判定・顧客入力を初期値としてプレフィル（業者は現地確認で修正するだけにする）
  useEffect(() => {
    if (!c || vcPrefilled) return;
    setVcModel(c.ai_model_guess ?? c.customer_specs?.model_number ?? "");
    setVcCapacity(c.ai_capacity ?? c.customer_specs?.capacity ?? "");
    setVcRefrigerant(c.customer_specs?.refrigerant ?? "");
    setVcPrefilled(true);
  }, [c, vcPrefilled]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["case", caseId] });
    qc.invalidateQueries({ queryKey: ["my-bids"] });
  };

  const confirmMut = useMutation({ mutationFn: () => api.confirmSchedule(caseId, date), onSuccess: invalidate });
  const completeMut = useMutation({
    mutationFn: async () => {
      // 実機確認値の登録（学習ラベル）は「完了報告」を絶対に止めない：失敗しても握りつぶして先へ進む。
      if ([vcModel, vcCapacity, vcRefrigerant].some((s) => s.trim() !== "")) {
        try {
          await api.reportVendorConfirmed(caseId, {
            model_number: vcModel,
            capacity: vcCapacity,
            refrigerant: vcRefrigerant,
          });
        } catch (e) {
          console.warn("[complete] vendor_confirmed 登録に失敗（完了報告は継続）:", e);
        }
      }
      await api.reportCompleted(caseId); // ← ここが本体。status→completed で顧客へ通知＆画面遷移。
    },
    onSuccess: invalidate,
  });

  if (isLoading || !c) return null;

  const ChatButton = inChat ? null : (
    <button
      onClick={onOpenChat}
      className="w-full flex items-center justify-center gap-2 border border-brand-500 text-brand-600 dark:text-brand-200 text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
    >
      <MessageCircle size={15} /> お客様とチャットする
    </button>
  );
  // チャット内に埋め込む時は余白を詰める（チャットのfooter枠が既に余白を持つ）。
  const pad = inChat ? "space-y-3" : "px-4 pb-4 -mt-1 space-y-3";

  const status = (c as Case).status;

  // 工事完了済み
  if (status === "completed" || status === "paid") {
    return (
      <div className={pad}>
        <span className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 dark:text-brand-300">
          <CheckCircle2 size={13} /> 工事完了報告済み
        </span>
        {!inChat && <MutualDeleteBar c={c as Case} />}
        {ChatButton}
      </div>
    );
  }

  return (
    <div className={pad}>
      {status === "scheduled" ? (
        <>
          <div className="text-xs text-ink-soft dark:text-white/60">
            工事日：{c.scheduled_date ? new Date(c.scheduled_date).toLocaleDateString("ja-JP") : "未設定"}（確定済み）
          </div>
          {c.scheduled_date && (
            <CalendarButtons
              dateISO={c.scheduled_date}
              title="シタミー エアコン工事"
              location={[c.prefecture, c.city, c.address].filter(Boolean).join("") || undefined}
              details={"シタミー（sirasu）でマッチングした工事です。\n案件ID: " + caseId}
            />
          )}
          {/* 実機の最終確認値（品番/能力/冷媒）: 完了報告と一緒に送信。AI判定精度向上の正解データになる。 */}
          <div className="rounded-xl border border-[var(--color-border)] p-3 space-y-2">
            <p className="text-xs font-bold flex items-center gap-1.5">
              <ClipboardCheck size={14} className="text-brand-500" /> 実機の最終確認（完了報告と一緒に送信）
            </p>
            <p className="text-[11px] text-ink-soft dark:text-white/60 leading-relaxed">
              現地で確認した実機の値に修正してください。AI判定の答え合わせに使われ、判定精度の向上につながります。
            </p>
            <input
              type="text"
              value={vcModel}
              onChange={(e) => setVcModel(e.target.value)}
              placeholder="品番（例: MSZ-GE2224-W）"
              className="w-full border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={vcCapacity}
                onChange={(e) => setVcCapacity(e.target.value)}
                placeholder="能力（例: 2.2kW）"
                className="flex-1 min-w-0 border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
              />
              <input
                type="text"
                value={vcRefrigerant}
                onChange={(e) => setVcRefrigerant(e.target.value)}
                placeholder="冷媒（例: R32）"
                className="flex-1 min-w-0 border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
              />
            </div>
          </div>
          <button
            onClick={() => completeMut.mutate()}
            disabled={completeMut.isPending}
            className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
          >
            <Flag size={15} /> {completeMut.isPending ? "送信中..." : "工事完了を報告する"}
          </button>
          {completeMut.error && <p className="text-xs text-red-600">{(completeMut.error as Error).message}</p>}
        </>
      ) : (
        <>
          <div className="text-xs font-bold text-ink-soft dark:text-white/60">工事日を確定してください</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="flex-1 border border-[var(--color-border)] rounded-md px-2 py-2 bg-transparent text-sm"
            />
            <button
              onClick={() => confirmMut.mutate()}
              disabled={!date || confirmMut.isPending}
              className="flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-bold px-3 py-2 rounded-xl active:scale-95 transition whitespace-nowrap"
            >
              <CalendarCheck size={15} /> {confirmMut.isPending ? "確定中..." : "工事日確定"}
            </button>
          </div>
          {confirmMut.error && <p className="text-xs text-red-600">{(confirmMut.error as Error).message}</p>}
        </>
      )}
      {!inChat && <MutualDeleteBar c={c as Case} />}
      {ChatButton}
    </div>
  );
}

/**
 * チャット開始前の注意（この案件で初回1回のみ表示。以降は自動でチャットが開く）。
 * 目的：AI/顧客入力の品番等は誤りがあり得るため、チャットで実機を必ず確認してから工事する、という注意喚起。
 */
function EquipmentConfirmGate({ onConfirm }: { onConfirm: () => void }) {
  return (
    <div className="rounded-2xl border border-brand-500/40 bg-brand-50 dark:bg-brand-800/20 p-4 space-y-3">
      <p className="text-sm font-bold flex items-center gap-1.5 text-brand-700 dark:text-brand-200">
        <ClipboardCheck size={16} /> チャットを始める前に（この案件で1回だけ）
      </p>
      <p className="text-sm text-ink dark:text-white/80 leading-relaxed">
        表示中の <b>品番・能力・冷媒</b> は、AI解析やお客様の入力による<b>参考値です（誤りがある場合があります）</b>。
        トラブル防止のため、チャットや写真でお客様と実機を確認してから工事・発注を進めてください。
      </p>
      <p className="text-[11px] text-ink-soft dark:text-white/50">
        ※ この案内はこの案件では次回から表示されません。
      </p>
      <button
        onClick={onConfirm}
        className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 text-white text-sm font-bold py-2.5 rounded-xl active:scale-95 transition"
      >
        <MessageCircle size={15} /> 了解してチャットを開く
      </button>
    </div>
  );
}

function StatusIcon({ status, size = 20 }: { status: string; size?: number }) {
  if (status === "accepted") return <CheckCircle2 size={size} className="text-brand-500" />;
  if (status === "rejected" || status === "withdrawn") return <XCircle size={size} className="text-ink-soft" />;
  return <Clock size={size} className="text-yellow-600" />;
}

/** 代表写真サムネイル（受注管理の各案件。無い/読込前はプレースホルダ）。 */
function CaseThumb({ url }: { url?: string }) {
  return (
    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-white/10 border border-[var(--color-border)] flex items-center justify-center shrink-0">
      {url ? (
        <img src={url} alt="案件写真" className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <ImageIcon size={18} className="text-ink-soft/40 dark:text-white/30" />
      )}
    </div>
  );
}

function labelOf(s: string) {
  return s === "accepted" ? "受注確定" : s === "rejected" ? "他社確定" : s === "withdrawn" ? "取下げ" : "選定待ち";
}
